import { attributeLabel } from '@shared/attributes/catalog';
import { sourceLabel } from '@shared/sources';
import type { CanonicalField, JsonValue, ValueMethod } from '@shared/types';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, Mail, Sparkles, UserRound, X } from 'lucide-react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Alert, Badge, Spinner } from '../../components/ui/feedback';
import { api } from '../../lib/api';
import { cn } from '../../lib/cn';
import { queryKeys } from '../../lib/query';
import { errorMessage } from '../../lib/session';
import type { AccountDetailResponse } from './types';

/**
 * How a value was obtained, in the user's words. Facts found in a source are never presented the
 * same way as AI output — that distinction is the whole point of this panel.
 */
const METHOD_LABEL: Record<ValueMethod, { label: string; tone: 'success' | 'accent' | 'warning' | 'neutral' }> = {
  native: { label: 'From source', tone: 'success' },
  parsed: { label: 'Parsed from page', tone: 'success' },
  enriched: { label: 'Found on website', tone: 'success' },
  derived: { label: 'Derived from collected data', tone: 'accent' },
  imported: { label: 'Imported', tone: 'neutral' },
  ai_extracted: { label: 'AI-extracted (quote verified)', tone: 'warning' },
  ai_inferred: { label: 'AI-inferred — not source data', tone: 'warning' },
  user_edited: { label: 'Edited by you', tone: 'neutral' },
};

function renderValue(value: JsonValue): string {
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) return value.map((item) => renderValue(item)).join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/** Distinct sources behind a value: one source seen in two runs is still one source. */
function distinctSources(field: CanonicalField): Array<{ source: string; url?: string }> {
  const seen = new Map<string, { source: string; url?: string }>();
  for (const ref of field.sources) {
    const key = `${ref.source}|${ref.url ?? ''}`;
    if (!seen.has(key)) seen.set(key, { source: ref.source, ...(ref.url ? { url: ref.url } : {}) });
  }
  return [...seen.values()];
}

function FieldRow({ attribute, field }: { attribute: string; field: CanonicalField }) {
  const method = METHOD_LABEL[field.method];
  const sources = distinctSources(field);
  const isAi = field.method === 'ai_extracted' || field.method === 'ai_inferred';

  return (
    <div className="border-b border-border py-2 last:border-b-0">
      <div className="flex items-start justify-between gap-3">
        <span className="text-[13px] text-muted-foreground">{attributeLabel(attribute)}</span>
        <Badge tone={method.tone}>{method.label}</Badge>
      </div>
      <p className={cn('mt-0.5 text-[13px] text-foreground', isAi && 'italic')}>{field.display ?? renderValue(field.value)}</p>

      {field.evidence ? (
        <p className="mt-1 text-[11px] text-muted-foreground">
          {field.evidence.basis === 'inferred' ? 'Inferred: ' : 'Basis: '}
          {field.evidence.summary}
          {field.confidence !== undefined ? ` (confidence ${String(Math.round(field.confidence * 100))}%)` : ''}
        </p>
      ) : null}

      {sources.length > 0 ? (
        <ul className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
          {sources.map((ref) => (
            <li key={`${ref.source}-${ref.url ?? ''}`} className="text-[11px] text-muted-foreground">
              {ref.url ? (
                <a className="inline-flex items-center gap-0.5 hover:text-accent hover:underline" href={ref.url} target="_blank" rel="noreferrer noopener">
                  {sourceLabel(ref.source)}
                  <ExternalLink className="h-2.5 w-2.5" aria-hidden="true" />
                </a>
              ) : (
                sourceLabel(ref.source)
              )}
            </li>
          ))}
        </ul>
      ) : null}

      {field.conflicts && field.conflicts.length > 0 ? (
        <p className="mt-1 text-[11px] text-warning">
          Other sources reported: {field.conflicts.map((conflict) => `${renderValue(conflict.value)} (${sourceLabel(conflict.source)})`).join('; ')}
        </p>
      ) : null}
    </div>
  );
}

export interface AccountDrawerProps {
  id: string;
  onClose: () => void;
}

/** Side panel showing every collected value for one business, and the evidence behind it. */
export function AccountDrawer({ id, onClose }: AccountDrawerProps) {
  const navigate = useNavigate();
  const detail = useQuery({
    queryKey: queryKeys.account(id),
    queryFn: () => api.get<AccountDetailResponse>(`/api/accounts/${encodeURIComponent(id)}`),
  });

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const account = detail.data?.account;
  const data = account?.data ?? {};
  const customKeys = Object.keys(data).filter((key) => key.startsWith('custom.'));
  const standardKeys = Object.keys(data).filter((key) => !key.startsWith('custom.') && !key.startsWith('meta.'));

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <button type="button" className="absolute inset-0 bg-black/30" aria-label="Close details" onClick={onClose} />
      <aside
        className="relative z-10 flex h-full w-full max-w-[560px] flex-col overflow-y-auto border-l border-border bg-surface shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-label={account ? `Details for ${account.name}` : 'Business details'}
      >
        <header className="sticky top-0 flex items-start justify-between gap-3 border-b border-border bg-surface px-4 py-3">
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold text-foreground">{account?.name ?? 'Loading…'}</h2>
            {account ? (
              <p className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[13px] text-muted-foreground">
                {[account.category, account.city].filter(Boolean).join(' · ')}
                {account.sources.map((source) => (
                  <Badge key={source} tone="neutral">
                    {sourceLabel(source)}
                  </Badge>
                ))}
              </p>
            ) : null}
          </div>
          <span className="flex shrink-0 items-center gap-1">
            {/* Only offered when there is somewhere to send it — otherwise it is a dead button. */}
            {account?.email ? (
              <button
                type="button"
                className="flex items-center gap-1.5 rounded-[var(--radius-control)] border border-border-strong px-2.5 py-1 text-[13px] font-medium text-foreground transition-colors duration-150 hover:bg-muted"
                onClick={() => void navigate(`/outreach?account=${encodeURIComponent(id)}`)}
              >
                <Mail className="h-3.5 w-3.5" aria-hidden="true" />
                Write email
              </button>
            ) : null}
            <button
              type="button"
              className="flex h-8 w-8 items-center justify-center rounded-[var(--radius-control)] text-muted-foreground hover:bg-muted hover:text-foreground"
              onClick={onClose}
              aria-label="Close"
              autoFocus
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </span>
        </header>

        <div className="flex-1 px-4 py-3">
          {detail.isPending ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Spinner /> Loading details…
            </div>
          ) : detail.isError || !account ? (
            <Alert tone="error">{errorMessage(detail.error) ?? 'These details could not be loaded.'}</Alert>
          ) : (
            <>
              {customKeys.length > 0 ? (
                <section className="mb-4">
                  <h3 className="mb-1 flex items-center gap-1.5 text-[13px] font-semibold text-foreground">
                    <Sparkles className="h-3.5 w-3.5" aria-hidden="true" /> Custom attributes
                  </h3>
                  <div className="rounded-[var(--radius-panel)] border border-border px-3">
                    {customKeys.map((key) => {
                      const value = data[key];
                      return value ? <FieldRow key={key} attribute={key} field={value} /> : null;
                    })}
                  </div>
                </section>
              ) : null}

              <section className="mb-4">
                <h3 className="mb-1 text-[13px] font-semibold text-foreground">Collected data</h3>
                <div className="rounded-[var(--radius-panel)] border border-border px-3">
                  {standardKeys.map((key) => {
                    const value = data[key];
                    return value ? <FieldRow key={key} attribute={key} field={value} /> : null;
                  })}
                </div>
              </section>

              {detail.data.people.length > 0 ? (
                <section className="mb-4">
                  <h3 className="mb-1 text-[13px] font-semibold text-foreground">People ({detail.data.people.length})</h3>
                  <ul className="space-y-1.5">
                    {detail.data.people.map((person) => (
                      <li key={person.id} className="flex items-start gap-2 rounded-[var(--radius-panel)] border border-border px-3 py-2">
                        <UserRound className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                        <span className="min-w-0 flex-1">
                          <span className="block text-[13px] font-medium text-foreground">{person.fullName}</span>
                          {person.title ? <span className="block text-[13px] text-muted-foreground">{person.title}</span> : null}
                          <span className="mt-0.5 block text-[11px] text-muted-foreground">
                            {person.sourceUrl ? (
                              <a className="hover:text-accent hover:underline" href={person.sourceUrl} target="_blank" rel="noreferrer noopener">
                                Found on {sourceLabel(person.source)}
                              </a>
                            ) : (
                              `Found on ${sourceLabel(person.source)}`
                            )}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}

              <section>
                <h3 className="mb-1 text-[13px] font-semibold text-foreground">Where this came from</h3>
                <ul className="space-y-1.5">
                  {detail.data.records.map((record) => (
                    <li key={record.id} className="rounded-[var(--radius-panel)] border border-border px-3 py-2">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[13px] font-medium text-foreground">{sourceLabel(record.source)}</span>
                        <Badge tone={record.status === 'merged' ? 'success' : record.status === 'failed' ? 'danger' : 'neutral'}>
                          {record.status}
                        </Badge>
                      </div>
                      <p className="mt-0.5 text-[11px] text-muted-foreground">
                        {record.fields.length} field{record.fields.length === 1 ? '' : 's'} ·{' '}
                        {new Date(record.createdAt).toLocaleString()}
                      </p>
                      {record.sourceUrl ? (
                        <a
                          className="mt-0.5 inline-flex items-center gap-0.5 break-all text-[11px] text-accent hover:underline"
                          href={record.sourceUrl}
                          target="_blank"
                          rel="noreferrer noopener"
                        >
                          {record.sourceUrl}
                          <ExternalLink className="h-2.5 w-2.5 shrink-0" aria-hidden="true" />
                        </a>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </section>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
